import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "en", label: "English" },
  { code: "pl", label: "Polski" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
];

export default function LanguageSwitcher({ light = false }: { light?: boolean }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const current =
    languages.find((l) => l.code === i18n.language) || languages[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
          light
            ? "border-background-50/40 bg-background-50/15 text-background-50 hover:bg-background-50/25"
            : "border-foreground-200 bg-background-50/80 text-foreground-800 hover:bg-background-100"
        }`}
        aria-label="Language"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-global-line text-base"></i>
        </span>
        <span className="font-semibold">{current.code.toUpperCase()}</span>
        <span className="w-3 h-3 flex items-center justify-center">
          <i className="ri-arrow-down-s-line text-sm"></i>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-40 overflow-hidden rounded-lg border border-foreground-200 bg-background-50 py-1 shadow-none">
          {languages.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => {
                i18n.changeLanguage(l.code);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap ${
                l.code === current.code
                  ? "font-semibold text-primary-700"
                  : "text-foreground-700"
              }`}
            >
              <span>{l.label}</span>
              {l.code === current.code && (
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-primary-600"></i>
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}